/**
 * Attribution Tracking Utility
 * Captures UTM parameters, GCLID, FBCLID, referrer, and landing page on first touch
 * Persists through session storage for form submission
 */

const STORAGE_KEY = 'gray_attribution';

/**
 * Get URL parameters from current location
 */
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    gclid: params.get('gclid'),
    fbclid: params.get('fbclid'),
  };
};

/**
 * Generate a simple session ID
 */
const generateSessionId = () => {
  return 'sess_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
};

/**
 * Initialize attribution tracking on page load
 * Only captures on first touch (doesn't overwrite existing session data)
 */
export const initAttribution = () => {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    
    if (!existing) {
      // First touch - capture everything
      const urlParams = getUrlParams();
      const attribution = {
        landing_page: window.location.pathname + window.location.search,
        referrer: document.referrer || null,
        session_id: generateSessionId(),
        captured_at: new Date().toISOString(),
        ...urlParams,
      };
      
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
      
      // Also store in localStorage as backup (survives tab close)
      const lsKey = 'gray_attribution_backup';
      if (!localStorage.getItem(lsKey)) {
        localStorage.setItem(lsKey, JSON.stringify(attribution));
      }
      
      console.log('[Attribution] First touch captured:', attribution);
    } else {
      console.log('[Attribution] Session exists, preserving first touch data');
    }
  } catch (e) {
    console.warn('[Attribution] Storage error:', e);
  }
};

/**
 * Get attribution data for form submission
 */
export const getAttribution = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Fallback to localStorage backup
    const backup = localStorage.getItem('gray_attribution_backup');
    if (backup) {
      return JSON.parse(backup);
    }
  } catch (e) {
    console.warn('[Attribution] Error reading attribution:', e);
  }
  
  // Return minimal data if nothing stored
  return {
    landing_page: window.location.pathname,
    referrer: document.referrer || null,
    session_id: generateSessionId(),
  };
};

/**
 * Enrich form data with attribution
 */
export const enrichFormData = (formData, sourceForm = 'unknown') => {
  const attribution = getAttribution();
  
  return {
    ...formData,
    source_form: sourceForm,
    source_page: window.location.pathname,
    landing_page: attribution.landing_page,
    referrer: attribution.referrer,
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    utm_term: attribution.utm_term,
    utm_content: attribution.utm_content,
    gclid: attribution.gclid,
    fbclid: attribution.fbclid,
    session_id: attribution.session_id,
  };
};

export default {
  initAttribution,
  getAttribution,
  enrichFormData,
};
